var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import { Component, Model, Watch } from 'vue-property-decorator';
import PageHeader from '@/components/page-header/PageHeader';
import ReleaseRosterDialog from '@/views/RosterManagement/ReleaseRosterDialog';
import { defaultCalendarSettings, dayMap, businessHours } from '@/support/CalendarDefaults';
import ErrorHandler from '@/support/ErrorHandler';
import { Teacher } from '@/models/Teacher';
import { Subject } from '@/models/Subject';
import { Room } from '@/models/Room';
import { Level } from '@/models/Level';
import { Group } from '@/models/Group';
import AsyncSelect from '@/components/async-select/AsyncSelect';
import GenerateRosterDialog from './GenerateRosterDialog';
import { DateTime } from 'luxon';
import LoaderCard from '@/components/loader-card/LoaderCard';
import AddEventDialog from '@/components/mi-calendar/AddEventDialog';
import { parseToDateTimeslotModel } from '@/support/CalendarEventFunctions';
import { renderCustomTimeslot } from '@/support/CalendarEventRenderFunctions';
import ShowEventsDialog from './ShowEventsDialog';
import { DateTimeslotSummary } from '@/models/DateTimeslotSummary';
import ShowStudentsWithoutLessonsDialog from '@/views/RosterManagement/ShowStudentsWithoutLessonsDialog';
import AbstractUrl from '@/support/AbstractUrl';
import { isArray, isEmpty } from 'lodash';
let RosterManagement = class RosterManagement extends AbstractUrl {
    constructor() {
        super(...arguments);
        this.fullBusinessHours = {
            daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
            startTime: '00:00',
            endTime: '00:00',
        };
        this.isReleasingRoster = false;
        this.isGeneratingRosterDialog = false;
        this.isLoading = true;
        this.eventData = null;
        this.events = [];
        this.clearSelects = false;
        this.isUnsafeMode = false;
        this.isShowingEventsDialog = false;
        this.calendarOptionsUpdated = false;
        this.isEventEditable = false;
        this.showSafeModeButton = true;
        this.isShowingStudentsWithoutLessons = false;
        this.hasWeekends = false;
        this.subjectModel = new Subject;
        this.subjects = [];
        this.datetimeslots = [];
        this.roomModel = new Room;
        this.rooms = [];
        this.teacherModel = new Teacher;
        this.users = [];
        this.levelModel = new Level;
        this.levels = [];
        this.groupModel = new Group().sort('name');
        this.groups = [];
        this.selectedEventId = null;
        this.currentWeekStart = '';
        this.activitiesString = null;
        // TODO: TEMP VAR, needs to be API call
        this.warningMeta = {
            notifications: 1,
            warnings: 3,
            alerts: 2,
        };
    }
    get eventFilter() {
        return {
            users: this.users,
            rooms: this.rooms,
            levels: this.levels,
            groups: this.groups,
            subjects: this.subjects,
        };
    }
    get weekStart() {
        const setting = this.$store.state.settings.find((setting) => {
            return setting.key === 'first_day_of_week';
        });
        if (setting) {
            this.hasWeekends = this.hasWeekendsInSchoolTimes() ? this.hasWeekendsInSchoolTimes() : ['sunday', 'saturday'].includes(setting.value);
            return dayMap[setting.value];
        }
        return 1;
    }
    get businessHours() {
        if (!this.$store.state.openingHours) {
            return [];
        }
        return this.$store.state.openingHours;
    }
    get calendarOptions() {
        return {
            ...defaultCalendarSettings,
            ...{
                buttonText: {
                    today: this.$t('rosterManagement.today'),
                },
                editable: true,
                firstDay: this.weekStart,
                weekends: this.hasWeekends,
                eventOverlap: false,
                businessHours: this.fullBusinessHours,
                eventConstraint: 'businessHours',
                selectConstraint: 'businessHours',
                select: (selectionInfo) => {
                    // this.createCalendarEvent(selectionInfo);
                },
                eventClick: (selectionInfo) => {
                    this.selectedEventId = selectionInfo.event.id;
                    this.viewCalendarEvent(selectionInfo);
                },
                eventRender: (eventInfo) => {
                    if (!eventInfo.event.id) {
                        return true;
                    }
                    const type = this.filterType;
                    if (eventInfo.event.extendedProps.events.length < 2) {
                        this.activitiesString = this.$tc('roster.lessons', 1);
                    }
                    else {
                        this.activitiesString = this.$tc('roster.lessons', 2);
                    }
                    return renderCustomTimeslot(eventInfo, type, this.getLockClass(eventInfo), this.activitiesString);
                },
                eventDrop: (eventDropInfo) => {
                    this.updateEvent(parseToDateTimeslotModel(eventDropInfo), eventDropInfo);
                },
                eventResize: (eventResizeInfo) => {
                    this.updateEvent(parseToDateTimeslotModel(eventResizeInfo), eventResizeInfo);
                },
                datesRender: (info) => {
                    const calendarStart = DateTime.fromJSDate(info.view.activeStart).toFormat('yyyy-LL-dd');
                    if (this.currentWeekStart !== calendarStart) {
                        this.currentWeekStart = calendarStart;
                        const micalendar = this.$refs.rosterCalendar;
                        if (!micalendar || !micalendar.calendar) {
                            return;
                        }
                        const start = DateTime.fromJSDate(info.view.activeStart).toMillis();
                        const lock = DateTime.fromSQL(this.softLockDate || '').toMillis();
                        if (start > lock) {
                            micalendar.setOptions({ businessHours: businessHours(this.businessHours) });
                            this.showSafeModeButton = false;
                        }
                        else {
                            micalendar.setOptions({ businessHours: this.fullBusinessHours });
                            this.showSafeModeButton = true;
                        }
                        const calendarEnd = DateTime.fromJSDate(info.view.activeEnd).toFormat('yyyy-LL-dd');
                        this.fetchEvents(calendarStart, calendarEnd);
                    }
                },
            },
        };
    }
    get filterType() {
        if (this.users.length) {
            return 'users';
        }
        else if (this.rooms.length) {
            return 'rooms';
        }
        else if (this.levels.length) {
            return 'levels';
        }
        else if (this.groups.length) {
            return 'groups';
        }
        else if (this.subjects.length) {
            return 'subjects';
        }
        else {
            return null;
        }
    }
    get hasWarnings() {
        return this.warningMeta.notifications > 0 || this.warningMeta.alerts > 0 || this.warningMeta.warnings > 0;
    }
    get breadcrumbs() {
        return [{ name: this.$t('rosterManagement.title').toString(), path: '/roster/management' }];
    }
    get softLockDate() {
        const soft_lock = this.$store.state.settings.find((setting) => {
            return setting.key === 'soft_lock_date';
        });
        return soft_lock.value + ' 23:59:59';
    }
    get hardLockDate() {
        const hard_lock = this.$store.state.settings.find((setting) => {
            return setting.key === 'hard_lock_date';
        });
        return hard_lock.value + ' 23:59:59';
    }
    mounted() {
        const currentDayNumber = parseInt(DateTime.local().toFormat('c'));
        this.currentWeekStart = DateTime.local().minus({ days: currentDayNumber - 1 }).toFormat('yyyy-LL-dd');
        const end = DateTime.local().plus({ days: 7 - currentDayNumber }).toFormat('yyyy-LL-dd');
        this.fetchEvents(this.currentWeekStart, end);
        this.isLoading = true;
        if (this.$route.query && !isEmpty(this.$route.query)) {
            this.$nextTick(() => {
                this.preSetFilters(this.$route.query);
                this.preFillSelects(this.$route.query);
                this.isLoading = true;
            });
            return;
        }
    }
    fetchEvents(starts, ends) {
        this.events = [];
        new DateTimeslotSummary()
            .filter({ starts_at: starts, ends_at: ends })
            .limit(100)
            .load()
            .then((response) => {
            this.datetimeslots = response.data.data;
            const summary = new DateTimeslotSummary({ data: response.data.data, meta: response.data.meta });
            this.events = summary.calendarEvents;
            this.isLoading = false;
            this.clearSelects = false;
        })
            .catch((error) => {
            ErrorHandler.network(error);
        });
    }
    fetchDetailedEvents(starts, ends) {
        this.events = [];
        new DateTimeslotSummary()
            .filter({ ...this.eventFilter, ...{ starts_at: starts, ends_at: ends } })
            .limit(100)
            .load()
            .then((response) => {
            const summary = new DateTimeslotSummary({ data: response.data.data, meta: response.data.meta });
            this.events = summary.calendarEvents;
            this.isLoading = false;
        });
        // .catch((error: AxiosError) => {
        //   ErrorHandler.network(error);
        // });
    }
    handleDateTimeslotRemoved() {
        const end = DateTime.fromFormat(this.currentWeekStart, 'yyyy-LL-dd').plus({ days: 7 }).toFormat('yyyy-LL-dd');
        this.fetchEvents(this.currentWeekStart, end);
    }
    preSetFilters(filters) {
        if (filters.subjects) {
            this.subjects = isArray(filters.subjects) ? filters.subjects : [filters.subjects];
        }
        if (filters.rooms) {
            this.rooms = filters.rooms;
        }
        if (filters.levels) {
            this.levels = isArray(filters.levels) ? filters.levels : [filters.levels];
        }
        if (filters.groups) {
            this.groups = isArray(filters.groups) ? filters.groups : [filters.groups];
        }
        if (filters.users) {
            this.users = isArray(filters.users) ? filters.users : [filters.users];
        }
    }
    async preFillSelects(filters) {
        if (filters.subjects && this.$refs.subjectsSelect) {
            this.preFillSubjectsSelect(isArray(filters.subjects) ? filters.subjects : [filters.subjects]);
        }
        if (filters.rooms && this.$refs.roomsSelect) {
            this.preFillRoomsSelect(isArray(filters.rooms) ? filters.rooms : [filters.rooms]);
        }
        if (filters.levels && this.$refs.levelSelect) {
            this.preFillLevelSelect(isArray(filters.levels) ? filters.levels : [filters.levels]);
        }
        if (filters.groups && this.$refs.groupSelect) {
            this.preFillGroupSelect(isArray(filters.groups) ? filters.groups : [filters.groups]);
        }
        if (filters.users && this.$refs.usersSelect) {
            this.preFillUsersSelect(isArray(filters.users) ? filters.users : [filters.users]);
        }
    }
    async preFillRoomsSelect(roomIds) {
        const rooms = await new Room()
            .filter('ids', roomIds)
            .all();
        this.$refs.roomsSelect.setDefaultValue(rooms.map((room) => ({
            value: room.id,
            text: room.name,
        })), false);
    }
    async preFillSubjectsSelect(subjectIds) {
        const subjects = await new Subject()
            .filter('ids', subjectIds)
            .all();
        this.$refs.subjectsSelect.setDefaultValue(subjects.map((subject) => ({
            value: subject.id,
            text: subject.name,
        })), false);
    }
    async preFillLevelSelect(levelIds) {
        const levels = await new Level()
            .filter('ids', levelIds)
            .all();
        this.$refs.levelSelect.setDefaultValue(levels.map((level) => ({
            value: level.id,
            text: level.name,
        })), false);
    }
    async preFillGroupSelect(groupIds) {
        const groups = await new Group()
            .filter('ids', groupIds)
            .all();
        this.$refs.groupSelect.setDefaultValue(groups.map((group) => ({
            value: group.id,
            text: group.name,
        })), false);
    }
    async preFillUsersSelect(userIds) {
        const user = await new Group()
            .filter('ids', userIds)
            .all();
        this.$refs.usersSelect.setDefaultValue(user.map((user) => ({
            value: user.id,
            text: user.name,
        })), false);
    }
    hasWeekendsInSchoolTimes() {
        const saturday = this.$store.state.openingHours.find((hour) => {
            return hour.id === 'saturday';
        });
        const sunday = this.$store.state.openingHours.find((hour) => {
            return hour.id === 'sunday';
        });
        return sunday.starts_at !== '00:00:00' && sunday.ends_at !== '00:00:00' || saturday.starts_at !== '00:00:00' && saturday.ends_at !== '00:00:00';
    }
    // TODO: opening calendar on start day school;
    // protected get currentOpeningHour() {
    //   const hours = this.businessHours;
    //   if(!hours.length) {
    //     return {
    //       hours: '07',
    //       minutes: '00',
    //     }
    //   }
    //   const day = DateTime.local().toFormat('cccc').toLowerCase();
    //   const hour = hours.find((hour: OpeningHour) => {
    //     return hour.id === day;
    //   });
    //   if(!hour) {
    //     return {
    //       hours: '07',
    //       minutes: '00',
    //     }
    //   }
    //   return {
    //     hours: '07',
    //     minutes: '00',
    //   }
    // }
    getLockClass(eventInfo) {
        if (!this.softLockDate && !this.hardLockDate) {
            return '';
        }
        const eventStartDate = DateTime.fromMillis(Date.parse(eventInfo.event.start));
        const hardLockDate = DateTime.fromFormat(this.hardLockDate || '', 'yyyy-LL-dd HH:mm:ss');
        const softLockDate = DateTime.fromFormat(this.softLockDate || '', 'yyyy-LL-dd HH:mm:ss');
        if (eventStartDate.toMillis() <= hardLockDate.toMillis()) {
            return 'fc-hard_lock';
        }
        if (eventStartDate.toMillis() <= softLockDate.toMillis()) {
            return 'fc-soft_lock';
        }
        if (!eventInfo.event.extendedProps.is_valid) {
            return 'fc-needs_attention';
        }
        return '';
    }
    isBeforeDate(checkDate, referenceDate) {
        if (checkDate.month < referenceDate.month) {
            return true;
        }
        if (checkDate.month === referenceDate.month && checkDate.day <= referenceDate.day) {
            return true;
        }
        return false;
    }
    updateCalendar(event) {
        const eventsClone = [...this.events]; // clone
        const eventIndex = this.getEventIndex(eventsClone, event.id || '');
        if (eventIndex >= 0) {
            eventsClone[eventIndex] = event.calendarEvent;
        }
        this.$set(this, 'events', eventsClone);
    }
    eventEditable(eventDate, lockDate) {
        if (typeof lockDate !== 'string') {
            return true;
        }
        const lock = DateTime.fromString(lockDate, 'yyyy-LL-dd');
        const event = DateTime.fromJSDate(eventDate);
        if (event <= lock) {
            return false;
        }
        return true;
    }
    viewCalendarEvent(selectionInfo) {
        const start = DateTime.fromJSDate(selectionInfo.event.start).toMillis();
        const lock = DateTime.fromSQL(this.softLockDate || '').toMillis();
        if (this.isUnsafeMode || start > lock) {
            this.isEventEditable = true;
        }
        this.eventData = parseToDateTimeslotModel(selectionInfo);
        this.isShowingEventsDialog = true;
    }
    isFiltered() {
        return this.users.length || this.subjects.length || this.rooms.length || this.levels.length || this.groups.length;
    }
    removeEvent() {
        const eventIndex = this.getEventIndex(this.events, this.selectedEventId || '');
        if (eventIndex >= 0) {
            this.events.splice(eventIndex, 1);
        }
    }
    updateEvent(datetimeslot, calendarEvent) {
        datetimeslot
            .update()
            .then((updatedDateTimeslot) => {
        })
            .catch((error) => {
            ErrorHandler.network(error);
            calendarEvent.revert();
        });
    }
    resetFilters() {
        this.rooms = [];
        this.users = [];
        this.levels = [];
        this.groups = [];
        this.subjects = [];
        this.clearSelects = true;
        const micalendar = this.$refs.rosterCalendar;
        if (!micalendar || !micalendar.calendar) {
            return;
        }
        const starts = this.formatJSDate(micalendar.calendar.view.activeStart);
        const ends = this.formatJSDate(micalendar.calendar.view.activeEnd);
        this.isLoading = true;
        this.fetchEvents(starts, ends);
    }
    formatJSDate(date) {
        return DateTime.fromJSDate(date).toFormat('yyyy-LL-dd');
    }
    reRenderCalendar() {
        const micalendar = this.$refs.rosterCalendar;
        if (!micalendar || !micalendar.calendar) {
            return;
        }
        const starts = this.formatJSDate(micalendar.calendar.view.activeStart);
        const ends = this.formatJSDate(micalendar.calendar.view.activeEnd);
        this.fetchEvents(starts, ends);
    }
    dialogChanged(isShowing) {
        if (!isShowing) {
            this.isEventEditable = false;
        }
    }
    modeChanged(isUnsafeMode) {
        const micalendar = this.$refs.rosterCalendar;
        if (!micalendar || !micalendar.calendar) {
            return;
        }
        const options = !isUnsafeMode ?
            { businessHours: this.fullBusinessHours } :
            { businessHours: businessHours(this.businessHours) };
        micalendar.setOptions(options);
    }
    filterChanged(value) {
        if (value.length) {
            const micalendar = this.$refs.rosterCalendar;
            if (!micalendar || !micalendar.calendar) {
                return;
            }
            const filters = { ...this.eventFilter };
            this.setQueryParams(filters);
            const starts = this.formatJSDate(micalendar.calendar.view.activeStart);
            const ends = this.formatJSDate(micalendar.calendar.view.activeEnd);
            this.isLoading = true;
            this.fetchDetailedEvents(starts, ends);
        }
    }
    changedCurrentWeekStart() {
        this.isLoading = true;
    }
    getEventIndex(events, id) {
        return this.events.findIndex((element) => {
            return element.id === id;
        });
    }
};
__decorate([
    Model('teacher', { default: () => new Teacher() }),
    __metadata("design:type", typeof (_a = typeof Teacher !== "undefined" && Teacher) === "function" ? _a : Object)
], RosterManagement.prototype, "teacher", void 0);
__decorate([
    Watch('isShowingEventsDialog'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
], RosterManagement.prototype, "dialogChanged", null);
__decorate([
    Watch('isUnsafeMode'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
], RosterManagement.prototype, "modeChanged", null);
__decorate([
    Watch('rooms'),
    Watch('users'),
    Watch('groups'),
    Watch('levels'),
    Watch('subjects'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array]),
    __metadata("design:returntype", void 0)
], RosterManagement.prototype, "filterChanged", null);
__decorate([
    Watch('currentWeekStart'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], RosterManagement.prototype, "changedCurrentWeekStart", null);
RosterManagement = __decorate([
    Component({
        components: {
            PageHeader,
            ReleaseRosterDialog,
            GenerateRosterDialog,
            AsyncSelect,
            LoaderCard,
            AddEventDialog,
            ShowEventsDialog,
            ShowStudentsWithoutLessonsDialog,
        },
    })
], RosterManagement);
export default RosterManagement;
